import { useState, useEffect } from 'react';
import {
    Box,
    TextField,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Typography,
    CircularProgress,
    Chip,
    Paper,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

interface Drug {
    id: string;
    name: string;
    active_substances: string[];
    match_score: number;
}

interface DrugSearchProps {
    onSelectDrug: (drugId: string) => void;
    extractedDrugs?: string[];
    species?: string;
    moleculeQuery?: string;
}

export function DrugSearch({ onSelectDrug, extractedDrugs = [], species, moleculeQuery }: DrugSearchProps) {
    const { t } = useTranslation();
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<Drug[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (moleculeQuery) {
            setSearchQuery(moleculeQuery);
        }
    }, [moleculeQuery]);

    useEffect(() => {
        if (!searchQuery || searchQuery.length < 2) {
            setSearchResults([]);
            return;
        }

        const delayDebounce = setTimeout(async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await axios.get('/api/v1/drugs/search', {
                    params: {
                        q: searchQuery,
                        species: species || undefined,
                        limit: 10,
                    },
                });
                setSearchResults(response.data);
            } catch (err) {
                console.error('Drug search error:', err);
                setError(t('consultation.noDrugsFound', { query: searchQuery }));
            } finally {
                setLoading(false);
            }
        }, 300);

        return () => clearTimeout(delayDebounce);
    }, [searchQuery, species, t]);


    return (
        <Box>
            {/* Suggested drugs dropdown - compact */}
            {/* Suggested drugs list */}
            {extractedDrugs.length > 0 && (
                <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" color="text.secondary" gutterBottom>
                        {t('consultation.suggestedFromConsultation')}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {extractedDrugs.map((drug, index) => (
                            <Chip
                                key={index}
                                label={drug}
                                size="small"
                                variant="outlined"
                                onClick={() => setSearchQuery(drug)}
                                clickable
                                sx={{ fontSize: '0.75rem' }}
                            />
                        ))}
                    </Box>
                </Box>
            )}

            {/* Search input - compact */}
            <TextField
                fullWidth
                size="small"
                placeholder={t('consultation.searchDrugsPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                    startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                sx={{ mb: 1 }}
            />

            {/* Loading state */}
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                    <CircularProgress size={20} />
                </Box>
            )}

            {/* Error state */}
            {error && (
                <Typography color="error" variant="body2" sx={{ py: 1, fontSize: '0.875rem' }}>
                    {error}
                </Typography>
            )}

            {/* Search results - compact */}
            {!loading && searchResults.length > 0 && (
                <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto' }}>
                    <List dense disablePadding>
                        {searchResults.map((drug) => (
                            <ListItem key={drug.id} disablePadding>
                                <ListItemButton onClick={() => {
                                    onSelectDrug(drug.id);
                                    setSearchResults([]);
                                    setSearchQuery('');
                                }} sx={{ py: 0.5 }}>
                                    <ListItemText
                                        primary={<Typography variant="body2">{drug.name}</Typography>}
                                        secondary={<Typography variant="caption">{drug.active_substances.join(', ')}</Typography>}
                                    />
                                    <Chip
                                        label={`${drug.match_score}%`}
                                        size="small"
                                        color={drug.match_score > 80 ? 'success' : 'default'}
                                        sx={{ height: 20, fontSize: '0.7rem' }}
                                    />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            )}

            {/* No results */}
            {!loading && searchQuery.length >= 2 && searchResults.length === 0 && !error && (
                <Typography variant="caption" color="text.secondary" sx={{ py: 1, textAlign: 'center', display: 'block' }}>
                    {t('consultation.noDrugsFound', { query: searchQuery })}
                </Typography>
            )}
        </Box>
    );
}
